import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Grid, InputNumber, Typography, message, theme } from "antd";
import { CheckCircleFilled, ThunderboltOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useOfflineRead, useOnlineStatus, useOutbox } from "@dowty/offline";
import { activeEventCache, drinksCache, outbox } from "../offline/offline";
import { fetchActiveEvent, fetchDrinkCatalog } from "../offline/fetchers";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusTag } from "@dowty/ui";

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Fast presets for the common case at a bar - a beer, a shot, a mixed drink - plus a "Custom"
// slot that opens the input below rather than forcing everyone through it. Order matters: most
// likely first, so the common tap lands closest to the thumb.
const AMOUNT_PRESETS_ML = [330, 200, 500, 40, 700];

/** `/quick-add` - the fast path from the PWA's home-screen shortcut (see public/manifest.json).
 * Pick a drink, pick an amount, one tap to confirm. Works fully offline: the write always goes
 * to the outbox first (src/offline/offline.ts) and a flush is only attempted opportunistically
 * right after, never awaited before confirming to the user - see handleConfirm. */
const QuickAddPage: React.FC = () => {
    const { user } = useAuth();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const isOnline = useOnlineStatus();
    const { entries = [], retry, remove } = useOutbox(outbox);
    const userEntries = entries.filter(entry => entry.payload.user.userid === user?.userId);
    const failedEntries = userEntries.filter(entry => entry.status === "failed");
    const pendingCount = userEntries.length - failedEntries.length;
    const failedCount = failedEntries.length;
    const attentionNote = failedCount ? `, ${failedCount} need attention` : "";
    const [messageApi, contextHolder] = message.useMessage();

    const [selectedDrinkId, setSelectedDrinkId] = useState<number | null>(null);
    const [amountMl, setAmountMl] = useState<number | null>(null);
    const [customOpen, setCustomOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const { data: drinks, loading: drinksLoading, isFromCache: drinksFromCache, error: drinksError, reload: reloadDrinks } = useOfflineRead(
        drinksCache,
        `drinks-${user?.userId ?? "signed-out"}`,
        fetchDrinkCatalog,
        []
    );

    // Best-effort: if there's an active event, new drinks attach to it automatically (matching
    // what the event leaderboard expects). Offline, this just falls back to the cached value, or
    // to "no event" if there was never one cached - AddDrinkingEvent's eventId is optional.
    const activeEventFetcher = useCallback(async () => {
        if (!user) return undefined;
        return fetchActiveEvent(user);
    }, [user]);
    const { data: activeEvent } = useOfflineRead(
        activeEventCache,
        `activeEvent-${user?.userId ?? "signed-out"}`,
        activeEventFetcher,
        [user?.userId]
    );

    const selectedDrink = useMemo(
        () => drinks?.find((d) => d.drinkid === selectedDrinkId) ?? null,
        [drinks, selectedDrinkId]
    );

    const canConfirm = Boolean(user && selectedDrink && amountMl && amountMl > 0 && !saving);

    const handleConfirm = async () => {
        if (!user || !selectedDrink || !amountMl) return;
        setSaving(true);
        try {
            await outbox.enqueue({
                drink: {
                    drinkid: selectedDrink.drinkid,
                    drinkname: selectedDrink.drinkname,
                    promille: selectedDrink.promille,
                },
                amount: amountMl,
                // Stamped now, not at sync time, so a drink queued offline keeps its place in the history.
                tsInsert: Date.now(),
                eventId: activeEvent?.event?.eventid,
                user: { userid: user.userId, username: user.username },
            });
            messageApi.success(`${selectedDrink.drinkname} saved - will sync${isOnline ? "" : " when back online"}.`);
            // Reset the amount only; the same drink is often logged again right after (a round),
            // so leaving it selected saves the next tap.
            setAmountMl(null);
            setCustomOpen(false);
            // Fire-and-forget: don't make the user wait on a network round trip they already got
            // confirmation for. If this fails (still offline, flaky connection), the entry just
            // stays queued for the next automatic trigger (see useOutboxSync).
            void outbox.flush();
        } catch {
            messageApi.error(isGerman ? "Getränk konnte nicht gespeichert werden. Bitte erneut versuchen." : "Could not save drink. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="sf-content-column" style={{ padding: isMobile ? "8px 4px 24px" : "16px 24px 32px" }}>
            {contextHolder}
            <PageHeader title={<><ThunderboltOutlined aria-hidden="true" /> Quick add</>} subtitle="Pick a drink, pick an amount, confirm." />

            {drinksError && <ErrorState message={drinksError} retry={reloadDrinks} />}

            {!isOnline && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="warning"
                    showIcon
                    message="Offline"
                    description="This will be saved on your device and sent once you're back online."
                />
            )}
            {pendingCount > 0 && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type="info"
                    showIcon
                    message={`${pendingCount} drink${pendingCount === 1 ? "" : "s"} waiting to sync${attentionNote}`}
                />
            )}
            {failedCount > 0 && <Alert type="error" showIcon message={isGerman ? "Einige Getränke konnten nicht synchronisiert werden" : "Some drinks could not sync"} description={failedEntries.map(entry => <div key={entry.id}>{entry.payload.drink.drinkname} <Button size="small" style={{ minHeight: 44 }} onClick={() => retry(entry.id)}>{isGerman ? "Erneut versuchen" : "Retry"}</Button> <Button size="small" style={{ minHeight: 44 }} onClick={() => remove(entry.id)}>{isGerman ? "Entfernen" : "Remove"}</Button></div>)} />}

            <Text strong style={{ display: "block", marginBottom: 8 }}>Drink</Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                {drinksLoading && !drinks && <LoadingState variant="grid" label="Loading drinks" />}
                {drinks?.map((d) => {
                    const active = d.drinkid === selectedDrinkId;
                    return (
                        <Button
                            key={d.drinkid}
                            size="large"
                            type={active ? "primary" : "default"}
                            onClick={() => setSelectedDrinkId(d.drinkid)}
                            style={{ borderRadius: token.borderRadiusLG, maxWidth: "100%", height: "auto", whiteSpace: "normal", overflowWrap: "anywhere" }}
                            icon={active ? <CheckCircleFilled /> : undefined}
                        >
                            {d.drinkname}
                        </Button>
                    );
                })}
            </div>
            {!drinksLoading && !drinksError && drinks?.length === 0 && <EmptyState title="No drinks available" description="Add a drink before using quick add." />}
            {drinksFromCache && drinks && drinks.length > 0 && (
                <span style={{ display: "inline-block", marginTop: -12, marginBottom: 16 }}><StatusTag tone="info">Showing saved drink list</StatusTag></span>
            )}

            <Text strong style={{ display: "block", marginBottom: 8 }}>Amount (ml)</Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                {AMOUNT_PRESETS_ML.map((ml) => {
                    const active = !customOpen && amountMl === ml;
                    return (
                        <Button
                            key={ml}
                            size="large"
                            type={active ? "primary" : "default"}
                            onClick={() => { setAmountMl(ml); setCustomOpen(false); }}
                            style={{ borderRadius: token.borderRadiusLG }}
                        >
                            {ml} ml
                        </Button>
                    );
                })}
                <Button
                    size="large"
                    type={customOpen ? "primary" : "default"}
                    onClick={() => setCustomOpen(true)}
                    style={{ borderRadius: token.borderRadiusLG }}
                >
                    Custom
                </Button>
            </div>
            {customOpen && (
                <form onSubmit={event => { event.preventDefault(); if (canConfirm) void handleConfirm(); }}>
                <label htmlFor="quick-add-custom-amount">{isGerman ? "Menge in ml" : "Amount in ml"}</label>
                <InputNumber
                    id="quick-add-custom-amount"
                    autoFocus
                    size="large"
                    min={1}
                    max={5000}
                    style={{ width: "100%", marginBottom: 20 }}
                    placeholder="Amount in ml"
                    value={amountMl}
                    onChange={(v) => setAmountMl(v)}
                />
                </form>
            )}

            <Button
                type="primary"
                size="large"
                block
                disabled={!canConfirm}
                loading={saving}
                onClick={handleConfirm}
                style={{ height: 56, fontSize: 18, fontWeight: 600, marginTop: 8 }}
            >
                {selectedDrink && amountMl ? `Log ${selectedDrink.drinkname} - ${amountMl} ml` : "Log it"}
            </Button>
        </div>
    );
};

export default QuickAddPage;
